import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Printer, Share2 } from "lucide-react";
import { COUNTRIES, CONTINENTS, continentById } from "@/data/countries";
import { loadProgress, awardBadge } from "@/lib/progress";
import { grantXp } from "@/lib/progression";
import { getKidName } from "@/lib/kidProfile";

const KEY = "we_continent_certs_v1";

function readAwarded() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
}
function writeAwarded(next) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* quota */ }
}

/**
 * <ContinentCertWatcher /> — awards a printable continent-milestone
 * certificate the moment a child has learned every country in a
 * continent. Runs invisibly at the app root; only pops the modal
 * when a fresh continent is completed.
 *
 * Storage: `we_continent_certs_v1` = { [continentId]: dateISO }
 */
export default function ContinentCertWatcher() {
  const [cert, setCert] = useState(null); // { continent, count }

  useEffect(() => {
    const check = () => {
      const p = loadProgress();
      const learned = new Set(p.countriesLearned || []);
      const already = readAwarded();
      for (const cont of CONTINENTS) {
        if (already[cont.id]) continue;
        const countryCodes = COUNTRIES
          .filter((c) => c.continent === cont.id)
          .map((c) => c.code);
        if (countryCodes.length === 0) continue;
        const done = countryCodes.every((code) => learned.has(code));
        if (done) {
          const nextAwarded = { ...already, [cont.id]: new Date().toISOString() };
          writeAwarded(nextAwarded);
          awardBadge(`continent-${cont.id}`, `Explorer of ${cont.name}`);
          grantXp(300, `continent-${cont.id}`);
          setCert({ continent: cont, count: countryCodes.length });
          break; // only one per burst
        }
      }
    };
    check(); // Run once at mount for late-arrivals
    window.addEventListener("we-country-learned", check);
    return () => window.removeEventListener("we-country-learned", check);
  }, []);

  if (!cert) return null;
  const kidName = getKidName() || "Explorer";
  const contColor = cert.continent.color || "#22C55E";
  const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const doPrint = () => window.print();
  const doShare = async () => {
    const text = `${kidName} explored every country in ${cert.continent.name}! 🌍 (${cert.count} countries) — My World Quest`;
    try {
      if (navigator.share) await navigator.share({ title: "Continent Explorer!", text, url: window.location.origin });
      else await navigator.clipboard.writeText(text);
    } catch { /* cancelled */ }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[95] bg-slate-900/55 flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={() => setCert(null)}
        data-testid="continent-cert-overlay"
      >
        <motion.div
          initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="continent-cert"
          className="print-target relative w-full max-w-lg rounded-3xl border-4 border-slate-900 bg-white p-6 md:p-8 shadow-[10px_10px_0_0_rgba(15,23,42,1)] text-center"
        >
          <button
            type="button" onClick={() => setCert(null)}
            aria-label="Close"
            className="print:hidden absolute top-3 right-3 h-9 w-9 inline-flex items-center justify-center rounded-full border-[3px] border-slate-900 bg-white"
          >
            <X size={18} strokeWidth={3} />
          </button>
          {/* Bunting */}
          <div className="absolute -top-3 -left-3 text-4xl print:hidden" aria-hidden>🎉</div>
          <div className="absolute -top-3 -right-3 text-4xl print:hidden" aria-hidden>🌍</div>
          <div className="absolute -bottom-3 -left-3 text-4xl print:hidden" aria-hidden>🏆</div>
          <div className="absolute -bottom-3 -right-3 text-4xl print:hidden" aria-hidden>🎊</div>

          <div
            className="mx-auto w-20 h-20 rounded-full border-[4px] border-slate-900 flex items-center justify-center text-5xl"
            style={{ backgroundColor: contColor }}
          >
            {cert.continent.emoji || "🌐"}
          </div>
          <p className="mt-3 text-xs font-black uppercase tracking-widest text-emerald-800">
            Continent Explorer Certificate
          </p>
          <h2 className="mt-1 font-heading text-2xl md:text-3xl font-bold">
            Awarded to <span className="underline decoration-emerald-500 decoration-4 underline-offset-4">{kidName}</span>
          </h2>
          <p className="mt-2 text-sm md:text-base font-bold text-slate-700 leading-snug">
            For exploring every country in
          </p>
          <p className="mt-2 rounded-xl border-[3px] border-slate-900 bg-yellow-50 p-3 text-lg font-black text-slate-900">
            {cert.continent.emoji} {cert.continent.name}
            <span className="block text-xs font-bold text-slate-600 mt-0.5">
              {cert.count} countries · +300 XP
            </span>
          </p>
          <p className="mt-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            {dateStr} · My World Quest
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-2 print:hidden">
            <button
              type="button" onClick={doPrint}
              data-testid="continent-cert-print"
              className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-sm shadow-[0_4px_0_0_rgba(15,23,42,1)]"
            >
              <Printer size={14} strokeWidth={3} /> Print / PDF
            </button>
            <button
              type="button" onClick={doShare}
              className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_4px_0_0_rgba(15,23,42,1)]"
            >
              <Share2 size={14} strokeWidth={3} /> Share
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
